// Cosmic Compass - Main App Component (Refactored)
import React, { useState, useEffect, useCallback } from 'react';
import { AnimatePresence } from 'framer-motion';
import {
    doc,
    addDoc,
    collection,
    getDocs,
    query,
    serverTimestamp,
    deleteDoc,
    updateDoc,
    getDoc
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { ChevronLeft, Settings, Volume2, VolumeX, LogIn, LogOut } from 'lucide-react';

// Firebase & Utils
import { db, functions, appId } from './utils/firebase';
import {
    TAROT_DECK_NAMES,
    drawCardsWithOrientation,
    getCardEmoji
} from './utils/tarotData';
import { getTodayDateString, MYSTICAL_MESSAGE_UNAVAILABLE } from './utils/helpers';

// Hooks
import { useAuth } from './hooks/useAuth';
import { useSound } from './hooks/useSound';
import { useTone } from './hooks/useTone';

// Layout Components
import { CosmicBackground, Footer } from './components/layout/PageContainer';
import { Button } from './components/common/Button';
import { Modal, ConfirmModal } from './components/common/Modal';

// Screen Components
import { HomeScreen } from './components/screens/HomeScreen';
import { QuestionScreen } from './components/screens/QuestionScreen';
import { ReadingScreen } from './components/screens/ReadingScreen';
import { PastReadingsScreen } from './components/screens/PastReadingsScreen';
import { SettingsScreen } from './components/screens/SettingsScreen';
import { CardsListScreen } from './components/screens/CardsListScreen';
import { DisclaimerScreen } from './components/screens/DisclaimerScreen';
import { BirthChartScreen } from './components/screens/BirthChartScreen';
import { SynastryScreen } from './components/screens/SynastryScreen';
import { DailyCardModal } from './components/tarot/DailyCardModal';
import { PageLoader } from './components/common/LoadingSpinner';
import { InstallPrompt } from './components/common/InstallPrompt';
import { MarkdownText } from './components/common/MarkdownText';

export default function App() {
    // Screen & Navigation State
    const [currentScreen, setCurrentScreen] = useState('home');
    const [showDisclaimer, setShowDisclaimer] = useState(false);

    // Reading State
    const [userQuestion, setUserQuestion] = useState('');
    const [drawnCards, setDrawnCards] = useState([]);
    const [interpretation, setInterpretation] = useState('');
    const [actionableSteps, setActionableSteps] = useState(null);
    const [currentReadingId, setCurrentReadingId] = useState(null);

    // Manual Selection State
    const [isManualSelectionMode, setIsManualSelectionMode] = useState(false);
    const [manuallySelectedCards, setManuallySelectedCards] = useState(Array(5).fill(null));

    // Past Readings State
    const [pastReadings, setPastReadings] = useState([]);
    const [selectedPastReading, setSelectedPastReading] = useState(null);

    // Loading & Error State
    const [isLoading, setIsLoading] = useState(false);
    const [isSubLoading, setIsSubLoading] = useState(false);
    const [error, setError] = useState('');

    // Daily Card State
    const [isDailyCardModalOpen, setIsDailyCardModalOpen] = useState(false);
    const [isDailyCardLoading, setIsDailyCardLoading] = useState(false);

    // Modal State
    const [confirmModal, setConfirmModal] = useState({ isOpen: false, title: '', message: '', onConfirm: null });
    const [deeperDiveModal, setDeeperDiveModal] = useState({ isOpen: false, card: null, content: '' });
    const [actionableStepsModal, setActionableStepsModal] = useState({ isOpen: false, content: '' });

    // Custom Hooks
    const {
        currentUser,
        isAuthReady,
        dailyCardData,
        setDailyCardData,
        authError,
        setAuthError,
        handleGoogleLogin,
        handleLogout,
        loadUserDailyCard
    } = useAuth();

    const { isSoundEnabled, toggleSound, audioRef } = useSound();
    const { promptPrefix: tonePrefix } = useTone();

    // Combine auth error with app error
    useEffect(() => {
        if (authError) {
            setError(authError);
        }
    }, [authError]);

    // ============================================
    // Firestore Operations
    // ============================================

    const fetchPastReadings = useCallback(async () => {
        if (!isAuthReady || !currentUser?.uid) {
            setPastReadings([]);
            return;
        }
        setIsLoading(true);
        try {
            const readingsPath = `artifacts/${appId}/users/${currentUser.uid}/tarotReadings`;
            const q = query(collection(db, readingsPath));
            const querySnapshot = await getDocs(q);
            const readings = [];
            querySnapshot.forEach((docSnap) => {
                const data = docSnap.data();
                const cardsWithDetails = Array.isArray(data.cards) ? data.cards.map(card => ({
                    ...card,
                    emoji: card.emoji || getCardEmoji(card.name),
                    deeperInterpretation: card.deeperInterpretation || null
                })) : [];
                readings.push({
                    id: docSnap.id,
                    ...data,
                    cards: cardsWithDetails,
                    actionableSteps: data.actionableSteps || null
                });
            });
            readings.sort((a, b) => (b.timestamp?.seconds || 0) - (a.timestamp?.seconds || 0));
            setPastReadings(readings);
        } catch (err) {
            console.error("Error fetching past readings:", err);
            setError("Could not load past readings.");
            setPastReadings([]);
        }
        finally { setIsLoading(false); }
    }, [isAuthReady, currentUser]);

    useEffect(() => {
        if (isAuthReady && currentUser) {
            fetchPastReadings();
            if (!currentUser.isAnonymous) {
                loadUserDailyCard(currentUser.uid);
            }
        } else if (isAuthReady && !currentUser) {
            setPastReadings([]);
            setDailyCardData(null);
        }
    }, [isAuthReady, currentUser, fetchPastReadings, loadUserDailyCard, setDailyCardData]);

    const saveReading = useCallback(async (question, cards, newInterpretation) => {
        if (!isAuthReady || !currentUser?.uid) {
            setError("Could not save: User not authenticated.");
            return null;
        }
        let docRefId = null;
        try {
            const readingsPath = `artifacts/${appId}/users/${currentUser.uid}/tarotReadings`;
            const cardsToSave = cards.map(({ name, orientation, emoji, deeperInterpretation }) => ({
                name,
                orientation,
                emoji,
                deeperInterpretation: deeperInterpretation || null
            }));
            const docRef = await addDoc(collection(db, readingsPath), {
                question,
                cards: cardsToSave,
                interpretation: newInterpretation,
                actionableSteps: null,
                timestamp: serverTimestamp(),
                userId: currentUser.uid
            });
            docRefId = docRef.id;
        } catch (err) {
            console.error("Error saving reading:", err);
            setError("Could not save your reading.");
            return null;
        }
        if (docRefId) {
            await fetchPastReadings();
        }
        return docRefId;
    }, [isAuthReady, currentUser, fetchPastReadings]);

    // ============================================
    // AI API Calls
    // ============================================

    const callGeminiAPI = async (promptText) => {
        const getTarotReadingFunction = httpsCallable(functions, 'getTarotReading');
        try {
            const result = await getTarotReadingFunction({ promptText });
            if (result.data?.candidates?.[0]?.content?.parts?.[0]?.text) {
                return result.data.candidates[0].content.parts[0].text;
            }
            throw new Error("Invalid AI response structure.");
        } catch (error) {
            console.error("Error calling getTarotReading:", error);
            throw new Error(`Failed to get cosmic guidance: ${error.message}`);
        }
    };

    // ============================================
    // Reading Flow
    // ============================================

    const getReading = useCallback(async (useManualCards = false) => {
        if (!userQuestion.trim()) { setError("Please ask a question."); return; }
        if (!currentUser) { setError("User not available. Please wait or refresh."); return; }

        setIsLoading(true);
        setError('');
        setInterpretation('');
        setActionableSteps(null);
        setCurrentReadingId(null);
        setSelectedPastReading(null);

        let currentDrawnCardsArray;

        if (useManualCards && isManualSelectionMode) {
            const filledManualCards = manuallySelectedCards.filter(card => card?.name && card?.orientation);
            if (filledManualCards.length === 5) {
                currentDrawnCardsArray = filledManualCards.map(card => ({
                    name: card.name,
                    orientation: card.orientation,
                    emoji: card.emoji || getCardEmoji(card.name),
                    deeperInterpretation: null
                }));
            } else {
                setError("Please select all 5 cards for a manual reading.");
                setIsLoading(false);
                return;
            }
        } else {
            currentDrawnCardsArray = drawCardsWithOrientation(TAROT_DECK_NAMES, 5);
        }

        setDrawnCards(currentDrawnCardsArray);

        const cardsStringForPrompt = currentDrawnCardsArray.map(card => `${card.name} (${card.orientation})`).join(", ");
        const prompt = `${tonePrefix} The user asked: "${userQuestion}". The 5 tarot cards drawn are: ${cardsStringForPrompt}. Provide a cohesive and insightful tarot reading based on these cards and the question, considering each card's orientation. Focus on guidance and reflection. When mentioning a card, state its full name and orientation. Finish with: "Seeker, I invite you — tap any of the five cards above and let its hidden wisdom reveal itself."`;

        try {
            const text = await callGeminiAPI(prompt);
            setInterpretation(text);
            const newReadingId = await saveReading(userQuestion, currentDrawnCardsArray, text);
            if (newReadingId) setCurrentReadingId(newReadingId);
            setCurrentScreen('reading');
        } catch (err) {
            setError(`Failed to get interpretation: ${err.message}`);
            setDrawnCards([]);
        } finally {
            setIsLoading(false);
        }
    }, [userQuestion, saveReading, currentUser, isManualSelectionMode, manuallySelectedCards, tonePrefix]);

    // ============================================
    // Deeper Dive & Actionable Steps
    // ============================================

    const handleCardClickForDeeperDive = useCallback(async (clickedCard) => {
        const questionForPrompt = selectedPastReading?.question || userQuestion;
        if (!questionForPrompt) {
            setDeeperDiveModal({ isOpen: true, card: clickedCard, content: "A question context is needed." });
            return;
        }

        const relevantCards = selectedPastReading?.cards || drawnCards;
        if (!relevantCards?.length) {
            setDeeperDiveModal({ isOpen: true, card: clickedCard, content: "Card data unavailable." });
            return;
        }

        setDeeperDiveModal({ isOpen: true, card: clickedCard, content: '' });
        setError('');
        setIsSubLoading(true);

        if (selectedPastReading) {
            const cardFromPast = selectedPastReading.cards.find(c => c.name === clickedCard.name && c.orientation === clickedCard.orientation);
            setDeeperDiveModal({
                isOpen: true,
                card: clickedCard,
                content: cardFromPast?.deeperInterpretation || MYSTICAL_MESSAGE_UNAVAILABLE
            });
            setIsSubLoading(false);
            return;
        }

        const cardInState = drawnCards.find(c => c.name === clickedCard.name && c.orientation === clickedCard.orientation);
        if (cardInState?.deeperInterpretation) {
            setDeeperDiveModal({ isOpen: true, card: clickedCard, content: cardInState.deeperInterpretation });
            setIsSubLoading(false);
            return;
        }

        const otherCardsString = drawnCards.filter(card => card.name !== clickedCard.name).map(card => `${card.name} (${card.orientation})`).join(", ");
        const prompt = `${tonePrefix} The user asked: "${questionForPrompt}". The spread included ${clickedCard.name} (${clickedCard.orientation}). Other cards: ${otherCardsString || 'none'}. Provide a deeper interpretation of ${clickedCard.name} in relation to the question and other cards. Make it 2-3 paragraphs.`;

        try {
            const detailedText = await callGeminiAPI(prompt);
            setDeeperDiveModal({ isOpen: true, card: clickedCard, content: detailedText });

            const updatedCards = drawnCards.map(card =>
                card.name === clickedCard.name && card.orientation === clickedCard.orientation
                    ? { ...card, deeperInterpretation: detailedText }
                    : card
            );
            setDrawnCards(updatedCards);

            if (currentReadingId && currentUser?.uid) {
                const readingDocRef = doc(db, `artifacts/${appId}/users/${currentUser.uid}/tarotReadings`, currentReadingId);
                const cardsForFirestore = updatedCards.map(({ name, orientation, emoji, deeperInterpretation }) => ({
                    name, orientation, emoji, deeperInterpretation: deeperInterpretation || null
                }));
                await updateDoc(readingDocRef, { cards: cardsForFirestore });
            }
        } catch (err) {
            setDeeperDiveModal({ isOpen: true, card: clickedCard, content: `Error: ${err.message}` });
        } finally {
            setIsSubLoading(false);
        }
    }, [userQuestion, drawnCards, currentReadingId, currentUser, selectedPastReading, tonePrefix]);

    const handleGetActionableSteps = useCallback(async () => {
        const questionForPrompt = selectedPastReading?.question || userQuestion;
        const interpretationForPrompt = selectedPastReading?.interpretation || interpretation;
        const relevantCards = selectedPastReading?.cards || drawnCards;

        if (!questionForPrompt || !relevantCards?.length || !interpretationForPrompt) {
            setActionableStepsModal({ isOpen: true, content: "Cannot suggest steps without full context." });
            return;
        }

        setDeeperDiveModal({ isOpen: false, card: null, content: '' });
        setActionableStepsModal({ isOpen: true, content: '' });
        setIsSubLoading(true);
        setError('');

        if (selectedPastReading) {
            setActionableStepsModal({
                isOpen: true,
                content: selectedPastReading.actionableSteps || MYSTICAL_MESSAGE_UNAVAILABLE
            });
            setIsSubLoading(false);
            return;
        }

        if (actionableSteps) {
            setActionableStepsModal({ isOpen: true, content: actionableSteps });
            setIsSubLoading(false);
            return;
        }

        const cardsString = drawnCards.map(card => `${card.name} (${card.orientation})`).join(", ");
        const prompt = `${tonePrefix} User asked: "${questionForPrompt}". Cards drawn: ${cardsString}. Interpretation: "${interpretationForPrompt}". Suggest 2-3 concrete, actionable steps the user could take. Present as a bulleted list.`;

        try {
            const steps = await callGeminiAPI(prompt);
            setActionableStepsModal({ isOpen: true, content: steps });
            setActionableSteps(steps);

            if (currentReadingId && currentUser?.uid) {
                const readingDocRef = doc(db, `artifacts/${appId}/users/${currentUser.uid}/tarotReadings`, currentReadingId);
                await updateDoc(readingDocRef, { actionableSteps: steps });
            }
        } catch (err) {
            setActionableStepsModal({ isOpen: true, content: `Error: ${err.message}` });
        } finally {
            setIsSubLoading(false);
        }
    }, [userQuestion, drawnCards, interpretation, actionableSteps, currentReadingId, currentUser, selectedPastReading, tonePrefix]);

    // ============================================
    // Daily Card
    // ============================================

    const handleDrawOrViewDailyCard = useCallback(async () => {
        if (!currentUser || currentUser.isAnonymous) {
            setError("Please sign in to view your Card of the Day.");
            return;
        }
        setIsDailyCardModalOpen(true);

        const todayStr = getTodayDateString();
        if (dailyCardData?.dateDrawn === todayStr) return;

        setIsDailyCardLoading(true);
        try {
            const singleCardArr = drawCardsWithOrientation(TAROT_DECK_NAMES, 1);
            if (!singleCardArr?.length) throw new Error("Failed to draw a card.");
            const newDailyCard = singleCardArr[0];

            const prompt = `${tonePrefix} The user drew ${newDailyCard.name} (${newDailyCard.orientation}) as their card of the day. Provide a concise, uplifting message for their day. Single paragraph.`;
            const interpretationText = await callGeminiAPI(prompt);

            const newDailyData = {
                card: { name: newDailyCard.name, orientation: newDailyCard.orientation, emoji: newDailyCard.emoji },
                interpretation: interpretationText,
                dateDrawn: todayStr,
            };

            const userDocRef = doc(db, "users", currentUser.uid);
            await updateDoc(userDocRef, { dailyGuidance: newDailyData });
            setDailyCardData(newDailyData);
        } catch (error) {
            console.error("Error with daily card:", error);
            setError(`Failed to unveil daily card: ${error.message}`);
            setDailyCardData(null);
        } finally {
            setIsDailyCardLoading(false);
        }
    }, [currentUser, dailyCardData, setDailyCardData, tonePrefix]);

    // ============================================
    // Navigation & Modals
    // ============================================

    const handleSelectPastReading = useCallback((reading) => {
        if ((selectedPastReading?.id === reading?.id) || !reading) {
            setSelectedPastReading(null);
            setCurrentReadingId(null);
            setDrawnCards([]);
            setInterpretation('');
            setActionableSteps(null);
            setUserQuestion('');
        } else {
            setSelectedPastReading(reading);
            setCurrentReadingId(reading.id);
            setDrawnCards(reading.cards?.map(c => ({ ...c, emoji: c.emoji || getCardEmoji(c.name) })) || []);
            setInterpretation(reading.interpretation || '');
            setActionableSteps(reading.actionableSteps || null);
            setUserQuestion(reading.question || '');
        }
    }, [selectedPastReading]);

    const navigate = useCallback(async (screen) => {
        setError('');
        setAuthError('');

        if (currentScreen === 'pastReadings' && selectedPastReading && screen !== 'pastReadings') {
            handleSelectPastReading(null);
        } else if (currentScreen === 'reading' && (screen === 'home' || screen === 'question')) {
            setInterpretation('');
            setDrawnCards([]);
            setActionableSteps(null);
            setCurrentReadingId(null);
            setSelectedPastReading(null);
            setUserQuestion('');
            setIsManualSelectionMode(false);
            setManuallySelectedCards(Array(5).fill(null));
        }

        if (screen === 'question') {
            setUserQuestion('');
            setCurrentReadingId(null);
            setInterpretation('');
            setDrawnCards([]);
            setActionableSteps(null);
            setSelectedPastReading(null);
        }

        if (screen === 'pastReadings' && currentScreen !== 'pastReadings' && currentUser?.uid) {
            await fetchPastReadings();
        }

        if (currentScreen !== screen) {
            if (['reading', 'pastReadings'].includes(currentScreen)) {
                setDeeperDiveModal({ isOpen: false, card: null, content: '' });
                setActionableStepsModal({ isOpen: false, content: '' });
            }
        }

        if (screen === 'home') {
            setIsManualSelectionMode(false);
            setManuallySelectedCards(Array(5).fill(null));
        }

        setCurrentScreen(screen);
    }, [currentScreen, selectedPastReading, handleSelectPastReading, fetchPastReadings, currentUser, setAuthError]);

    const clearHistoryAction = useCallback(async () => {
        if (!isAuthReady || !currentUser?.uid) return;
        setIsLoading(true);
        try {
            const readingsPath = `artifacts/${appId}/users/${currentUser.uid}/tarotReadings`;
            const q = query(collection(db, readingsPath));
            const snapshot = await getDocs(q);
            for (const docSnap of snapshot.docs) {
                await deleteDoc(doc(db, readingsPath, docSnap.id));
            }
            setPastReadings([]);
            setSelectedPastReading(null);
        } catch (err) {
            setError("Could not clear history.");
        }
        setIsLoading(false);
    }, [isAuthReady, currentUser]);

    const clearHistory = useCallback(() => {
        setConfirmModal({
            isOpen: true,
            title: "Confirm Clear History",
            message: "Are you sure you want to clear all past readings?\nThis action cannot be undone.",
            onConfirm: clearHistoryAction
        });
    }, [clearHistoryAction]);

    // ============================================
    // Render
    // ============================================

    const renderScreen = () => {
        if (showDisclaimer && currentScreen !== 'disclaimer') {
            return <DisclaimerScreen onAccept={() => { setShowDisclaimer(false); navigate('home'); }} />;
        }

        switch (currentScreen) {
            case 'home':
                return <HomeScreen
                    onBegin={() => navigate('question')}
                    onPastReadings={() => navigate('pastReadings')}
                    onGoToCardsList={() => navigate('cardsList')}
                    onGoToBirthChart={() => navigate('birthChart')}
                    onGoToSynastry={() => navigate('synastry')}
                    onDrawOrViewDailyCard={handleDrawOrViewDailyCard}
                    currentUser={currentUser}
                    dailyCardData={dailyCardData}
                    isDailyCardLoading={isDailyCardLoading}
                />;
            case 'question':
                return <QuestionScreen
                    question={userQuestion}
                    setQuestion={setUserQuestion}
                    onRead={getReading}
                    isLoading={isLoading}
                    isManualSelectionMode={isManualSelectionMode}
                    setIsManualSelectionMode={setIsManualSelectionMode}
                    manuallySelectedCards={manuallySelectedCards}
                    setManuallySelectedCards={setManuallySelectedCards}
                />;
            case 'reading':
                return <ReadingScreen
                    question={userQuestion}
                    cards={drawnCards}
                    interpretation={interpretation}
                    onDone={() => { setIsManualSelectionMode(false); setManuallySelectedCards(Array(5).fill(null)); navigate('home'); }}
                    onCardClick={handleCardClickForDeeperDive}
                    onGetActionableSteps={handleGetActionableSteps}
                />;
            case 'pastReadings':
                return <PastReadingsScreen
                    readings={pastReadings}
                    selectedPastReading={selectedPastReading}
                    onSelectReading={handleSelectPastReading}
                    isLoading={isLoading}
                    onClearHistory={clearHistory}
                    onCardClick={handleCardClickForDeeperDive}
                    onGetActionableSteps={handleGetActionableSteps}
                    currentUser={currentUser}
                />;
            case 'settings':
                return <SettingsScreen
                    isSoundEnabled={isSoundEnabled}
                    onToggleSound={toggleSound}
                    onClearHistory={clearHistory}
                    onShowDisclaimer={() => setCurrentScreen('disclaimer')}
                    currentUser={currentUser}
                    isLoading={isLoading}
                />;
            case 'cardsList':
                return <CardsListScreen />;
            case 'birthChart':
                return <BirthChartScreen currentUser={currentUser} />;
            case 'synastry':
                return <SynastryScreen currentUser={currentUser} />;
            case 'disclaimer':
                return <DisclaimerScreen onAccept={() => { setShowDisclaimer(false); navigate('home'); }} />;
            default:
                return <HomeScreen onBegin={() => navigate('question')} onPastReadings={() => navigate('pastReadings')} onGoToCardsList={() => navigate('cardsList')} onGoToBirthChart={() => navigate('birthChart')} onGoToSynastry={() => navigate('synastry')} onDrawOrViewDailyCard={handleDrawOrViewDailyCard} currentUser={currentUser} dailyCardData={dailyCardData} isDailyCardLoading={isDailyCardLoading} />;
        }
    };

    return (
        <div className="min-h-screen text-amber-100 font-sans flex flex-col items-center justify-center p-4 overflow-hidden relative">
            {/* Ambient Audio */}
            <audio ref={audioRef} src="/Tarot.mp3" loop />

            {/* Animated Background */}
            <CosmicBackground />

            {/* Header */}
            <header className="fixed top-4 left-4 right-4 z-50 flex justify-between items-center">
                {/* Back Button */}
                <div>
                    {currentScreen !== 'home' && currentScreen !== 'disclaimer' && (
                        <Button
                            variant="icon"
                            icon={ChevronLeft}
                            onClick={() => {
                                if (currentScreen === 'pastReadings' && selectedPastReading) {
                                    handleSelectPastReading(null);
                                } else {
                                    navigate('home');
                                }
                            }}
                            aria-label="Back"
                        />
                    )}
                </div>

                {/* Right Controls */}
                <div className="flex items-center gap-2">
                    {/* Auth */}
                    {isAuthReady && currentUser && !currentUser.isAnonymous ? (
                        <>
                            <span className="block text-xs sm:text-sm text-amber-200/80 mr-1 max-w-[80px] sm:max-w-none truncate">
                                {currentUser.displayName?.split(' ')[0] || 'Seeker'}
                            </span>
                            <Button
                                variant="icon"
                                icon={LogOut}
                                onClick={async () => { await handleLogout(); navigate('home'); }}
                                aria-label="Logout"
                                className="bg-red-500/20 border-red-500/30 hover:bg-red-500/30"
                            />
                        </>
                    ) : (
                        isAuthReady && currentScreen === 'home' && (
                            <Button
                                variant="icon"
                                icon={LogIn}
                                onClick={async () => { await handleGoogleLogin(); }}
                                aria-label="Login with Google"
                                className="bg-green-500/20 border-green-500/30 hover:bg-green-500/30"
                            />
                        )
                    )}

                    {/* Sound Toggle */}
                    {currentScreen !== 'disclaimer' && (
                        <Button
                            variant="icon"
                            icon={isSoundEnabled ? Volume2 : VolumeX}
                            onClick={toggleSound}
                            aria-label={isSoundEnabled ? "Mute" : "Unmute"}
                            className={isSoundEnabled ? '' : 'text-purple-400'}
                        />
                    )}

                    {/* Settings */}
                    {currentScreen !== 'settings' && currentScreen !== 'disclaimer' && (
                        <Button
                            variant="icon"
                            icon={Settings}
                            onClick={() => navigate('settings')}
                            aria-label="Settings"
                        />
                    )}
                </div>
            </header>

            {/* Main Content */}
            <main className="w-full max-w-4xl z-0 relative mt-16 flex-1">
                {/* Error Banner */}
                <AnimatePresence>
                    {error && !deeperDiveModal.isOpen && !actionableStepsModal.isOpen && !confirmModal.isOpen && !isDailyCardModalOpen && (
                        <div className="mb-4 p-3 bg-red-500/80 backdrop-blur-sm text-white rounded-lg text-center text-sm">
                            {error}
                        </div>
                    )}
                </AnimatePresence>

                {/* Screen Content */}
                {isAuthReady ? (
                    <AnimatePresence mode="wait">
                        {renderScreen()}
                    </AnimatePresence>
                ) : (
                    <PageLoader text="Awakening the spirits..." />
                )}
            </main>

            {/* Footer */}
            {currentScreen !== 'disclaimer' && (
                <Footer userId={currentUser?.uid} />
            )}

            {/* Modals */}
            <ConfirmModal
                isOpen={confirmModal.isOpen}
                title={confirmModal.title}
                message={confirmModal.message}
                onConfirm={() => { confirmModal.onConfirm?.(); setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null }); }}
                onClose={() => setConfirmModal({ isOpen: false, title: '', message: '', onConfirm: null })}
            />

            <Modal
                isOpen={deeperDiveModal.isOpen}
                onClose={() => { setDeeperDiveModal({ isOpen: false, card: null, content: '' }); setError(''); }}
                title={`✨ Deeper Insight: ${deeperDiveModal.card?.emoji || ''} ${deeperDiveModal.card?.name || ''}`}
                isLoading={isSubLoading && deeperDiveModal.content !== MYSTICAL_MESSAGE_UNAVAILABLE}
            >
                <MarkdownText>{deeperDiveModal.content}</MarkdownText>
            </Modal>

            <Modal
                isOpen={actionableStepsModal.isOpen}
                onClose={() => { setActionableStepsModal({ isOpen: false, content: '' }); setError(''); }}
                title="✨ Suggested Actions & Reflections"
                isLoading={isSubLoading && actionableStepsModal.content !== MYSTICAL_MESSAGE_UNAVAILABLE}
            >
                <MarkdownText>{actionableStepsModal.content}</MarkdownText>
            </Modal>

            <DailyCardModal
                isOpen={isDailyCardModalOpen}
                onClose={() => setIsDailyCardModalOpen(false)}
                cardData={dailyCardData}
                isLoading={isDailyCardLoading}
            />

            {/* PWA Install Prompt - shows on home screen after disclaimer */}
            <InstallPrompt show={currentScreen === 'home' && !showDisclaimer} />
        </div>
    );
}